import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ScopeService } from '../../../../@core/utils';
import { NbButtonModule, NbCardModule, NbDialogRef, NbDialogService, NbSpinnerModule, NbTooltipModule } from '@nebular/theme';
import { CommonModule, NgComponentOutlet } from '@angular/common';
import { InvestmentWizardService } from '../../../../@core/utils/investment-wizard-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SideFormDialogComponent } from '../../side-forms/side-form-dialog/side-form-dialog.component';
import { filter, finalize, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-investment-wizard-root',
  standalone: true,
  imports: [CommonModule, NbButtonModule, TranslateModule, NbCardModule, NbSpinnerModule, NbTooltipModule],
  template: `
    <div class="scrolling-wrapper-wizard">
      @if (wizardService.currentStep(); as step) {
        <nb-card style="width: 50%; min-width: 600px; height: 80vh;" [nbSpinner]="isSubmitting()" nbSpinnerSize="giant">
          <nb-card-header class="d-flex {{ existingInvestment ? '' : 'py-3'}}" style="min-height: 50px;">
            <div class="col-11 text-center fw-normal" style="color: #dc3545;">
              {{ step['label'] }}
            </div>
            <div class="col-1 justify-content-end text-align-right text-center fs-5 pointer" (click)="close()">
              <i class="fal fa-x"></i>
            </div>
          </nb-card-header>
          <div class="flex-grow-1 d-flex wrapper" data-testid="parent-component" style="overflow-y: auto;">
            <ng-container *ngComponentOutlet="
              step['component'];
            "
            />
          </div>
          <nb-card-footer class="d-flex justify-content-evenly p-4 investment wizard" style="border-top: 1px solid #e6e6e6;">
          @if (existingInvestment) {
            <button nbButton class="mx-2 wizard-next" data-testid="previous"
              (click)="validateStep()"
              [disabled]="canGoNextState().isBlocked"
              *ngIf="wizardService.showNext()">
            {{ 'Save' | translate }}
            </button>
          } @else {
            <button nbButton class="mx-2 wizard-prev" data-testid="previous" (click)="wizardService.prev()"
                    [disabled]="wizardService.isPrevDisabled() || !wizardService.showPrev()">{{ 'Previous' | translate }}
            </button>
            <button nbButton class="mx-2 investment-next wizard-next" data-testid="next " (click)="next()"
                  [disabled]="canGoNextState().isBlocked"
                  *ngIf="wizardService.showNext()">
              {{ 'Next' | translate }}
            </button>
            <button nbButton class="mx-2 investment-next wizard-next" data-testid="submit"
                  [disabled]="canGoNextState().isBlocked"
                    (click)="createInvestment()"
                    *ngIf="wizardService.showSubmit()">{{ 'Create investment' | translate }}
            </button>
          }
          </nb-card-footer>
        </nb-card>
      }
    </div>
`,
  styleUrls: ['../../side-forms/base-form/base-form.component.scss', 'wizard-index.component.scss'],
})
export class InvestmentWizardRootComponent implements OnInit, OnDestroy {

  @ViewChild(NgComponentOutlet, { static: true }) ref: NgComponentOutlet;

  // internal state
  isSubmitting = computed(() => this.wizardService.isSubmitting());
  canGoNextState = computed(() => this.wizardService.canGoNext());
  scope = inject(ScopeService);

  dialogRef = inject(NbDialogRef);
  dialogService = inject(NbDialogService);
  translate = inject(TranslateService);
  wizardService = inject(InvestmentWizardService);
  existingInvestment: boolean = false;
  private destroyRef = inject(DestroyRef);

  close() {
    if (this.wizardService.stateIsEmpty() || this.existingInvestment) {
      this.dialogRef.close(null);
      return;
    }
    const ref = this.dialogService.open(
      SideFormDialogComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          title: this.translate.instant('Unregistered changes'),
          content: this.translate.instant('Would you like to exit ?'),
        },
      }
    )
    ref.onClose.pipe(filter(confirmation => !!confirmation)).subscribe(() => {
      this.dialogRef.close();
    });
  }

  ngOnInit(): void {
    this.dialogRef.onBackdropClick.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.close());
    if (this.wizardService.investmentId) {
      this.existingInvestment = true;
    }
  }

  ngOnDestroy(): void {
    this.wizardService.onDestroy();
  }

  next() {
    this.wizardService.next();
  }

  createInvestment() {
    this.wizardService.isSubmitting.set(true);
    this.wizardService.submit().pipe(
      finalize(() => this.wizardService.isSubmitting.set(false)),
      filter(result => !!result),
      tap(() => this.dialogRef.close(true))
    ).subscribe()
  }

  /**
   * For an existing investment, if the user validates the step, we send it back to the
   * investment form.
   */
  validateStep() {
    const currentStep = this.wizardService.currentStep();
    const currentState = this.wizardService.getState(currentStep.key);
    this.dialogRef.close(currentState);
  }
}
